import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Pencil, MapPin, Briefcase, Clock, Euro, Calendar,
  Building, CheckCircle, Save, Send, ChevronRight,
  ThumbsUp, Gift, Info, Star, FileText, Loader2
} from 'lucide-react';
import jobService, { CreateJobData, Job, JobResponse } from '../../services/jobService';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import DatePicker from '../Common/DatePicker';
import RichTextEditor, { sanitizeRichHtml } from '../Common/RichTextEditor';
import api from '../../services/api';
import CitySelectOptions from '../Common/CitySelectOptions';
import './JobFormPreview.scss';

// Treat a string as HTML if it contains any tag-like markup; otherwise it's
// legacy plain text from before the rich editor existed and we simply convert
// newlines to <br> on display.
const looksLikeHtml = (s: string): boolean => /<[a-z][\s\S]*>/i.test(s);

const capitalizeFirstLt = (value: string): string => {
  if (!value) return value;
  return value.charAt(0).toLocaleUpperCase('lt-LT') + value.slice(1);
};

const renderHtmlOrPlainText = (value: string): string => {
  if (!value) return '';
  if (looksLikeHtml(value)) return sanitizeRichHtml(value);
  return sanitizeRichHtml(value.replace(/\n/g, '<br>'));
};

interface JobFormPreviewProps {
  job?: Job;
  onSuccess?: (job: Job) => void;
  onCancel?: () => void;
}

interface CVTemplates {
  [category: string]: Array<{ id: number; category: string; value: string }>;
}

const JOB_FORM_FIELD_ORDER = [
  'title',
  'description',
  'requirements',
  'company_offers',
  'location',
  'job_type',
  'work_time',
  'start_date_type',
  'salary_min',
  'application_deadline',
] as const;

const scrollToJobField = (field: string) => {
  window.requestAnimationFrame(() => {
    document
      .querySelector<HTMLElement>(`[data-job-field="${field}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
};

const firstMissingField = (errorKeys: string[]): string | undefined =>
  JOB_FORM_FIELD_ORDER.find((key) => errorKeys.includes(key)) ?? errorKeys[0];

// Rich-text fields (RichTextEditor) keep markup like "<p></p>" or "<p><br></p>"
// after the user deletes the text, so a plain trim() sees a non-empty string for a
// visually-empty field. Strip tags / entities to detect a genuinely empty value.
const richTextIsEmpty = (html?: string | null): boolean => {
  if (!html) return true;
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text === '';
};

const JobFormPreview: React.FC<JobFormPreviewProps> = ({ job, onSuccess, onCancel }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  
  const [formData, setFormData] = useState<CreateJobData>({
    title: '',
    company_name: '',
    website_url: '',
    job_language: 'Lietuvių',
    work_field: '',
    location: '',
    position_type: '',
    job_type: '',
    work_time: '',
    positions: '',
    description: '',
    requirements: '',
    salary_min: undefined,
    salary_max: undefined,
    salary_currency: 'EUR',
    hide_salary: false,
    hide_company: false,
    benefits: '',
    company_offers: '',
    guarantee_confidentiality: false,
    inform_selected_only: false,
    additional_info: '',
    start_date_type: '',
    application_deadline: '',
    company_activity: '',
    contact_email: '',
    company_description: '',
    client_id: null,
    hide_client: false,
    status: 'draft'
  });


  const [cvTemplates, setCvTemplates] = useState<CVTemplates>({});
  const [loading, setLoading] = useState(false);
  const submitLockRef = React.useRef(false);
  const createdJobIdRef = React.useRef<number | null>(job?.id ?? null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
  const [titleSearch, setTitleSearch] = useState('');
  const [showTitleDropdown, setShowTitleDropdown] = useState(false);
  const titleFieldRef = React.useRef<HTMLDivElement>(null);
  const [employerClients, setEmployerClients] = useState<Array<{ id: number; name: string }>>([]);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const isRecruitmentAgency = !!user?.is_recruitment_agency;

  // Load CV templates and employer clients
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const response = await fetch(`${API_URL}/cv-templates?grouped=true`);
        if (response.ok) {
          const result = await response.json();
          setCvTemplates(result.data || {});
        }
      } catch (error) {
        console.error('Error loading CV templates:', error);
      }
    };
    loadTemplates();
  }, []);

  useEffect(() => {
    if (!isRecruitmentAgency) return;
    const loadClients = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const res = await fetch(`${API_URL}/employer-clients`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const result = await res.json();
          if (result.success) setEmployerClients(result.data);
        }
      } catch {}
      finally { setClientsLoaded(true); }
    };
    loadClients();
  }, [isRecruitmentAgency]);

  // Prefill company info from user profile (only for new jobs)
  useEffect(() => {
    if (user && !job) {
      const userAny = user as any;
      const profile = userAny.profile || {};
      
      // Profile may store company description as 'bio'
      const profileCompanyDescription = profile.company_description || profile.bio || '';
      
      setFormData(prev => ({
        ...prev,
        company_name: userAny.company_name || profile.company_name || prev.company_name,
        website_url: userAny.website_url || profile.website_url || prev.website_url,
        contact_email: userAny.email || prev.contact_email,
        company_description: userAny.company_description || profileCompanyDescription || prev.company_description,
        company_activity: userAny.company_activity || profile.company_activity || prev.company_activity,
      }));
    }
  }, [user, job]);

  // Populate form if editing existing job
  useEffect(() => {
    if (job) {
      const jobAny = job as any;
      setFormData({
        title: job.title || '',
        company_name: jobAny.company_name || '',
        website_url: jobAny.website_url || '',
        job_language: jobAny.job_language || 'Lietuvių',
        work_field: jobAny.work_field || '',
        location: job.location || '',
        position_type: jobAny.position_type || '',
        job_type: job.job_type || '',
        work_time: jobAny.work_time || '',
        positions: jobAny.positions || '',
        description: job.description || '',
        requirements: job.requirements || '',
        salary_min: job.salary_min,
        salary_max: job.salary_max,
        salary_currency: job.salary_currency || 'EUR',
        hide_salary: jobAny.hide_salary || false,
        hide_company: jobAny.hide_company || false,
        benefits: job.benefits || '',
        company_offers: jobAny.company_offers || '',
        guarantee_confidentiality: jobAny.guarantee_confidentiality || false,
        inform_selected_only: jobAny.inform_selected_only || false,
        additional_info: jobAny.additional_info || '',
        start_date_type: jobAny.start_date_type || '',
        application_deadline: jobAny.application_deadline ? jobAny.application_deadline.split('T')[0] : '',
        company_activity: jobAny.company_activity || '',
        contact_email: jobAny.contact_email || '',
        company_description: jobAny.company_description || '',
        client_id: jobAny.client_id || null,
        hide_client: jobAny.hide_client || false,
        status: (['closed', 'archived', 'declined'].includes(job.status) ? 'draft' : job.status) as 'draft' | 'pending' | 'published'
      });
    }
  }, [job]);

  // Close title dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (titleFieldRef.current && !titleFieldRef.current.contains(e.target as Node)) {
        setShowTitleDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredPositions = (cvTemplates.position || []).filter(p =>
    !titleSearch || p.value.toLowerCase().includes(titleSearch.toLowerCase())
  );
  const trimmedTitle = titleSearch.trim();
  const titleExactMatch = (cvTemplates.position || []).some(
    p => p.value.toLowerCase() === trimmedTitle.toLowerCase()
  );
  const canQueueNewTitle = trimmedTitle.length >= 2 && !titleExactMatch;

  const commitJobTitle = (title: string) => {
    const next = title.trim();
    if (!next) {
      stopEditing();
      setShowTitleDropdown(false);
      return;
    }
    setFormData(prev => ({ ...prev, title: next, positions: next }));
    setTitleSearch(next);
    if (validationErrors.has('title')) {
      setValidationErrors(prev => {
        const cleared = new Set(prev);
        cleared.delete('title');
        return cleared;
      });
    }
    stopEditing();
    setShowTitleDropdown(false);
  };

  const queueCustomTitle = async () => {
    if (!canQueueNewTitle) return;
    const name = trimmedTitle;
    commitJobTitle(name);
    try {
      await api.post('/cv-templates/pending', { category: 'position', value: name });
    } catch (error: any) {
      if (error.response?.status === 409) return;
    }
    showToast(
      `Pareigybė „${name}“ išsiųsta administratoriui. Ji taps prieinama sąraše, kai administratorius ją patvirtins.`,
      'info'
    );
  };

  const acceptTypedTitle = () => {
    const exact = filteredPositions.find(p => p.value.toLowerCase() === trimmedTitle.toLowerCase());
    if (exact) {
      commitJobTitle(exact.value);
      return;
    }
    if (filteredPositions.length > 0) {
      commitJobTitle(filteredPositions[0].value);
      return;
    }
    if (canQueueNewTitle) {
      void queueCustomTitle();
      return;
    }
    commitJobTitle(trimmedTitle);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name.includes('salary') && value ? parseFloat(value) || undefined : value)
    }));
    // Clear validation error for this field as the user interacts with it
    if (validationErrors.has(name)) {
      setValidationErrors(prev => { const next = new Set(prev); next.delete(name); return next; });
    }
  };

  const handleSubmit = async (action: 'save' | 'publish') => {
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    setLoading(true);
    let keepLock = false;

    try {
      // Drafts can be saved incomplete (the whole point — finish later). They
      // only need a title to be identifiable. Full mandatory-field validation
      // runs when publishing.
      if (action === 'save') {
        if (!formData.title?.trim()) {
          setValidationErrors(new Set(['title']));
          showToast('Įveskite bent pareigybės pavadinimą, kad galėtumėte išsaugoti juodraštį', 'error', 6000);
          scrollToJobField('title');
          return;
        }
        setValidationErrors(new Set());
      } else {
        const missingFields: string[] = [];
        const errorKeys: string[] = [];

        if (!formData.title?.trim())              { missingFields.push('Pareigybės pavadinimas'); errorKeys.push('title'); }
        if (richTextIsEmpty(formData.description))  { missingFields.push('Darbo aprašymas');        errorKeys.push('description'); }
        if (richTextIsEmpty(formData.requirements)) { missingFields.push('Reikalavimai kandidatams'); errorKeys.push('requirements'); }
        if (richTextIsEmpty(formData.company_offers)) { missingFields.push('Įmonė siūlo');           errorKeys.push('company_offers'); }
        if (!formData.location?.trim())           { missingFields.push('Darbovietės miestas');    errorKeys.push('location'); }
        if (!formData.job_type?.trim())           { missingFields.push('Darbo tipas');            errorKeys.push('job_type'); }
        if (!formData.work_time?.trim())          { missingFields.push('Darbo laikas');           errorKeys.push('work_time'); }
        if (!formData.start_date_type?.trim())    { missingFields.push('Darbo pradžia');          errorKeys.push('start_date_type'); }
        if (!formData.application_deadline?.trim()) { missingFields.push('Prašymus siųsti iki'); errorKeys.push('application_deadline'); }
        if (!formData.salary_min) { missingFields.push('Atlyginimas (nuo)'); errorKeys.push('salary_min'); }

        if (missingFields.length > 0) {
          setValidationErrors(new Set(errorKeys));
          showToast(`Užpildykite privalomus laukus: ${missingFields.join(', ')}`, 'error', 6000);
          const focusField = firstMissingField(errorKeys);
          if (focusField) scrollToJobField(focusField);
          return;
        }
        setValidationErrors(new Set());
      }

      const submitData: CreateJobData = {
        ...formData,
        hide_salary: false,
        status: action === 'publish' ? 'published' : 'draft'
      };

      let result: JobResponse;
      const existingId = job?.id ?? createdJobIdRef.current;
      if (existingId) {
        result = await jobService.updateJob(existingId, submitData);
      } else {
        result = await jobService.createJob(submitData);
      }

      if (result.success && result.data) {
        if (result.data.job?.id) createdJobIdRef.current = result.data.job.id;
        const successMessage = action === 'publish'
          ? 'Darbo skelbimas sėkmingai publikuotas!'
          : 'Juodraštis išsaugotas!';
        showToast(successMessage, 'success');

        // Keep the lock only while we navigate away, so a second click
        // cannot create a duplicate during the toast delay.
        keepLock = !!onSuccess;
        if (onSuccess) {
          setTimeout(() => onSuccess(result.data!.job), 800);
        }
      } else {
        showToast(result.message || 'Nepavyko išsaugoti darbo skelbimo', 'error');
      }
    } catch (error) {
      showToast('Įvyko klaida. Bandykite dar kartą.', 'error');
    } finally {
      setLoading(false);
      if (!keepLock) submitLockRef.current = false;
    }
  };

  const startEditing = (field: string) => setEditingField(field);
  const stopEditing = () => setEditingField(null);

  // Render editable text section
  const renderTextSection = (field: string, icon: React.ReactNode, label: string, placeholder: string, required?: boolean) => {
    const value = (formData as any)[field] || '';
    const isEditing = editingField === field;
    const hasError = validationErrors.has(field);

    const updateField = (html: string) => {
      setFormData(prev => ({ ...prev, [field]: html }));
      if (validationErrors.has(field)) {
        setValidationErrors(prev => { const next = new Set(prev); next.delete(field); return next; });
      }
    };

    return (
      <div className={`content-row${hasError ? ' field-error' : ''}`} data-job-field={field}>
        <div className="nav-item" onClick={() => !isEditing && startEditing(field)}>
          <div className="nav-icon">{icon}</div>
          <span>{label} {required && <span className="req">*</span>}</span>
        </div>
        <div className="content-box" onClick={() => !isEditing && startEditing(field)}>
          {isEditing ? (
            <div className="edit-mode edit-mode--rich" onClick={(e) => e.stopPropagation()}>
              <RichTextEditor
                value={value}
                onChange={updateField}
                placeholder={placeholder}
                autoFocus
                ariaLabel={label}
              />
              <button className="btn-done" onClick={stopEditing}>
                <CheckCircle size={14} /> Išsaugoti
              </button>
            </div>
          ) : (
            <div className={`content-text ${!value ? 'placeholder' : ''}`}>
              {value ? (
                <div
                  className="rich-text-content"
                  dangerouslySetInnerHTML={{ __html: renderHtmlOrPlainText(value) }}
                />
              ) : (
                <span className="click-hint"><Pencil size={12} /> {placeholder}</span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="job-form-preview-wysiwyg">
      {/* Breadcrumbs */}
      <div className="breadcrumbs">
        <span onClick={() => navigate('/')} className="breadcrumb-link">Pradžia</span>
        <ChevronRight size={14} className="separator" />
        <span onClick={() => navigate('/jobs?mode=employer')} className="breadcrumb-link">Skelbimai</span>
        <ChevronRight size={14} className="separator" />
        <span className="current">{job ? 'Redaguoti skelbimą' : 'Naujas skelbimas'}</span>
      </div>

      {/* ========== CLIENT SECTION (recruitment agencies only) ========== */}
      {isRecruitmentAgency && clientsLoaded && employerClients.length === 0 && (
        <div className="recruitment-no-clients-hint">
          <Info size={18} className="hint-icon" />
          <div className="hint-body">
            <strong>Nesukūrėte klientų</strong>
            <p>
              Jūsų paskyra pažymėta kaip personalo atrankos įmonė, tačiau dar neturite pridėtų klientų.
              Pridėkite klientą, jei norite priskirti šį skelbimą konkrečiai įmonei.
            </p>
            <a href="/employer/clients" className="hint-link">
              Eiti į Klientai &rarr;
            </a>
          </div>
        </div>
      )}
      {isRecruitmentAgency && employerClients.length > 0 && (
        <div className="form-section">
          <h2 className="section-title"><Building size={18} /> Klientas</h2>
          <div className="form-card">
            <div className="form-row">
              <div className="form-group">
                <label>Pasirinkite klientą</label>
                <select
                  value={formData.client_id || ''}
                  onChange={e => setFormData(prev => ({ ...prev, client_id: e.target.value ? parseInt(e.target.value) : null }))}
                >
                  <option value="">-- Nepasirinktas --</option>
                  {employerClients.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
            {formData.client_id && (
              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={!!formData.hide_client}
                    onChange={e => setFormData(prev => ({ ...prev, hide_client: e.target.checked }))}
                  />
                  Nerodyti kliento pavadinimo skelbime
                </label>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========== SECTION 2: JOB INFO ========== */}
      <div className="form-section">
        <h2 className="section-title"><Briefcase size={18} /> Informacija apie darbą</h2>

        {/* Job Title */}
        <div className={`title-field${validationErrors.has('title') ? ' field-error' : ''}`} data-job-field="title" ref={titleFieldRef} onClick={() => { startEditing('title'); setTitleSearch(formData.title || ''); setShowTitleDropdown(true); }}>
          {editingField === 'title' ? (
            <div className="edit-mode" onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                value={titleSearch}
                onChange={(e) => { setTitleSearch(e.target.value); setShowTitleDropdown(true); setFormData(prev => ({ ...prev, title: e.target.value })); if (validationErrors.has('title')) setValidationErrors(prev => { const next = new Set(prev); next.delete('title'); return next; }); }}
                placeholder="Įveskite pareigybės pavadinimą"
                autoFocus
                onFocus={() => setShowTitleDropdown(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    acceptTypedTitle();
                  }
                }}
              />
              {showTitleDropdown && (filteredPositions.length > 0 || canQueueNewTitle) && (
                <ul className="title-dropdown">
                  {filteredPositions.map(p => (
                    <li
                      key={p.id}
                      onMouseDown={(e) => { e.preventDefault(); commitJobTitle(p.value); }}
                    >
                      {p.value}
                    </li>
                  ))}
                  {canQueueNewTitle && (
                    <li
                      className="title-dropdown-add"
                      onMouseDown={(e) => { e.preventDefault(); void queueCustomTitle(); }}
                    >
                      + Pridėti „{trimmedTitle}“
                      <span>bus patvirtinta administratoriaus</span>
                    </li>
                  )}
                </ul>
              )}
            </div>
          ) : (
            <h1 className={!formData.title ? 'placeholder' : ''}>
              {formData.title || 'Pareigybės pavadinimas *'}
              <Pencil size={16} className="edit-icon" />
            </h1>
          )}
        </div>

        {/* Content Sections */}
        <div className="content-sections">
          {renderTextSection('description', <FileText size={18} />, 'Darbo aprašymas', 'Spustelėkite ir aprašykite darbo pareigas...', true)}
          {renderTextSection('requirements', <ThumbsUp size={18} />, 'Reikalavimai kandidatams', 'Spustelėkite ir įveskite reikalavimus...', true)}
          {renderTextSection('benefits', <Star size={18} />, 'Privalumai', 'Spustelėkite ir įveskite privalumus...')}
          {renderTextSection('company_offers', <Gift size={18} />, 'Įmonė siūlo', 'Spustelėkite ir įveskite ką siūlote...', true)}
          {renderTextSection('additional_info', <Info size={18} />, 'Papildoma informacija', 'Spustelėkite ir įveskite informaciją...')}
        </div>

        {/* Meta Fields (Always Expanded) */}
        <div className="meta-section-expanded">
          <div className="meta-grid">
            <div className={`meta-field${validationErrors.has('location') ? ' field-error' : ''}`} data-job-field="location">
              <label><MapPin size={14} /> Darbovietės miestas <span className="req">*</span></label>
              <select name="location" value={formData.location} onChange={handleInputChange}>
                <option value="">Pasirinkite</option>
                <CitySelectOptions
                  cities={cvTemplates.city || cvTemplates.work_location || []}
                  formatLabel={capitalizeFirstLt}
                />
              </select>
            </div>
            <div className={`meta-field${validationErrors.has('job_type') ? ' field-error' : ''}`} data-job-field="job_type">
              <label><Clock size={14} /> Darbo tipas <span className="req">*</span></label>
              <select name="job_type" value={formData.job_type} onChange={handleInputChange}>
                <option value="">Pasirinkite</option>
                {(cvTemplates.work_type || []).map((t, i) => (
                  <option key={i} value={t.value}>{capitalizeFirstLt(t.value)}</option>
                ))}
              </select>
            </div>
            <div className={`meta-field${validationErrors.has('work_time') ? ' field-error' : ''}`} data-job-field="work_time">
              <label><Clock size={14} /> Darbo laikas <span className="req">*</span></label>
              <select name="work_time" value={formData.work_time} onChange={handleInputChange}>
                <option value="">Pasirinkite</option>
                {(cvTemplates.work_time || []).map((t, i) => (
                  <option key={i} value={t.value}>{capitalizeFirstLt(t.value)}</option>
                ))}
              </select>
            </div>
            <div className={`meta-field${validationErrors.has('start_date_type') ? ' field-error' : ''}`} data-job-field="start_date_type">
              <label>Darbo pradžia <span className="req">*</span></label>
              <select name="start_date_type" value={formData.start_date_type} onChange={handleInputChange}>
                <option value="">Pasirinkite</option>
                {(cvTemplates.start_date_type || []).map((t, i) => (
                  <option key={i} value={t.value}>{capitalizeFirstLt(t.value)}</option>
                ))}
              </select>
            </div>
            <div className={`meta-field${validationErrors.has('salary_min') ? ' field-error' : ''}`} data-job-field="salary_min">
              <label><Euro size={14} /> Siūlomas atlyginimas (netto) EUR <span className="req">*</span></label>
              <div className="salary-inputs">
                <span>nuo</span>
                <input type="number" name="salary_min" value={formData.salary_min || ''} onChange={handleInputChange} placeholder="100" min="0" />
                <span>iki</span>
                <input type="number" name="salary_max" value={formData.salary_max || ''} onChange={handleInputChange} placeholder="5000" min="0" />
              </div>
            </div>
            <div className={`meta-field${validationErrors.has('application_deadline') ? ' field-error' : ''}`} data-job-field="application_deadline">
              <label><Calendar size={14} /> Prašymus siųsti iki <span className="req">*</span></label>
              <DatePicker
                value={formData.application_deadline || ''}
                onChange={(val) => { setFormData(prev => ({ ...prev, application_deadline: val })); if (validationErrors.has('application_deadline')) setValidationErrors(prev => { const next = new Set(prev); next.delete('application_deadline'); return next; }); }}
                placeholder="Pasirinkite datą"
                minDate={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>
        </div>

        {/* Checkboxes */}
        <div className="checkbox-section">
          <h3>Pažymėkite, jeigu norite, kad tai būtų rodoma skelbime:</h3>
          <div className="checkbox-row">
            <label><input type="checkbox" name="guarantee_confidentiality" checked={formData.guarantee_confidentiality} onChange={handleInputChange} /> Konfidencialumą garantuojame</label>
            <label><input type="checkbox" name="inform_selected_only" checked={formData.inform_selected_only} onChange={handleInputChange} /> Informuosime tik atrinktus kandidatus</label>
            <label><input type="checkbox" name="hide_company" checked={(formData as any).hide_company} onChange={handleInputChange} /> Nerodyti įmonės duomenų</label>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="form-actions">
        <button type="button" className="btn btn-cancel" onClick={onCancel} disabled={loading}>Atšaukti</button>
        <div className="action-buttons-right">
          <button type="button" className={`btn btn-save ${loading ? 'loading' : ''}`} onClick={() => handleSubmit('save')} disabled={loading}>
            {loading ? <Loader2 size={16} className="spinner" /> : <Save size={16} />}
            {loading ? 'Saugoma...' : 'Išsaugoti juodraštį'}
          </button>
          <button type="button" className={`btn btn-publish ${loading ? 'loading' : ''}`} onClick={() => handleSubmit('publish')} disabled={loading}>
            {loading ? <Loader2 size={16} className="spinner" /> : <Send size={16} />}
            {loading ? 'Publikuojama...' : 'Publikuoti'}
          </button>
        </div>
      </div>

      <p className="required-note">* privalomi laukai</p>
    </div>
  );
};

export default JobFormPreview;
